<script setup>
import { computed, ref, watch, nextTick, onMounted } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAbaPilula } from 'dashboard/routes/dashboard/legal/useAbaPilula';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: wootConstants.ASSIGNEE_TYPE.ME,
  },
});

const emit = defineEmits(['chatTabChange']);

const activeTabIndex = computed(() => {
  return props.items.findIndex(item => item.key === props.activeTab);
});

// A pilula deslizante do produto — a mesma da ficha do processo, do
// Financeiro e das Publicacoes. Aqui a barra e a NAVEGACAO da tela mais usada
// do dia: trocar de "Minhas" para "Fila" com a marca correndo ate o destino
// diz qual foi o clique. O sublinhado antigo aparecia de estalo, e em
// `#1f6feb`, um azul que nao e o nosso e que ficava igual no tema escuro.
const caixaAbas = ref(null);
const { estiloPilula, pilulaPronta, prepararPilula, aoRolar } =
  useAbaPilula(caixaAbas);

watch(
  () => props.activeTab,
  async () => {
    await nextTick();
    aoRolar();
  }
);

// As contagens chegam depois da primeira pintura e mudam a largura de cada
// aba ("Fila" vira "Fila 12"). Quando isso acontece a pilula tem que se
// recolocar SEM deslizar — e para isso que serve o `prepararPilula`.
watch(
  () => props.items.map(i => i.count).join(','),
  async () => {
    await nextTick();
    prepararPilula();
  }
);

onMounted(prepararPilula);

const onTabChange = selectedTabIndex => {
  if (selectedTabIndex >= 0 && selectedTabIndex < props.items.length) {
    const selectedItem = props.items[selectedTabIndex];
    if (selectedItem.key !== props.activeTab) {
      emit('chatTabChange', selectedItem.key);
    }
  }
};

const keyboardEvents = {
  'Alt+KeyN': {
    action: () => {
      if (props.activeTab === wootConstants.ASSIGNEE_TYPE.ALL) {
        onTabChange(0);
      } else {
        const nextIndex = (activeTabIndex.value + 1) % props.items.length;
        onTabChange(nextIndex);
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div class="ctt-trilho">
    <div ref="caixaAbas" class="ctt-caixa" role="tablist">
      <span
        class="aba-pilula"
        :class="{ 'aba-pilula--viva': pilulaPronta }"
        :style="estiloPilula"
        aria-hidden="true"
      />
      <button
        v-for="item in items"
        :key="item.key"
        class="aba ctt-aba"
        :class="{ 'aba--on': item.key === activeTab }"
        role="tab"
        :aria-selected="item.key === activeTab"
        @click="emit('chatTabChange', item.key)"
      >
        <span class="truncate">{{ item.name }}</span>
        <span v-if="item.count > 0" class="aba-cont">
          <span :key="item.count" class="aba-cont__n">
            {{
              item.count > 999 ? item.count.toLocaleString('pt-BR') : item.count
            }}
          </span>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Barra de Minhas / Fila / Todos.
 *
 * Usa a familia `.aba` do produto, mas com uma diferenca: aqui as tres abas
 * dividem a largura do painel em partes iguais (`flex: 1`), em vez de terem a
 * largura do proprio rotulo. Sao tres alvos fixos numa coluna estreita que a
 * pessoa acerta sem olhar — dividir por igual e o que torna isso possivel. */
.ctt-trilho {
  position: relative;
  flex-shrink: 0;
  padding: var(--esp-2);
  border-bottom: 1px solid rgb(var(--slate-3));
  /* A MESMA superfície da lista que a hospeda. Aqui estava `--solid-1` (e
     `--solid-2` no escuro), o cinza-chumbo: no tema escuro a barra era um
     retângulo de outro preto dentro da coluna navy. */
  background: rgb(var(--surface-2));
}

.ctt-caixa {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--esp-1);
}

.ctt-aba {
  flex: 1;
  justify-content: center;
  min-width: 0;
  padding: var(--esp-2);
}

/* Com a aba "Andie" (administrador) são quatro alvos: na coluna de 300px o
   "Todos 1.584" cortava. Coluna estreita, recuo e vão menores
   (17/09/2026). */
.ctt-caixa {
  container-type: inline-size;
}

@container (max-width: 320px) {
  .ctt-aba {
    gap: calc(var(--esp-1) / 2);
    padding-inline: calc(var(--esp-1) / 2);
  }
}

/* Em 1024px (coluna de 340px) as quatro partes iguais davam 76px a cada uma,
   e "Minhas 2" virava "Mi… 2" enquanto "IA 15" sobrava (medido 08/10/2026).
   Apertado, cada aba parte do próprio rótulo e só o que sobra é dividido. */
@container (max-width: 360px) {
  .ctt-aba {
    flex: 1 1 auto;
  }
}

/* Sem ícone, por pedido do Victor (16/09/2026): só o nome e o contador.
   Com ícone, na coluna de 288–340px os nomes viravam "Min…" — o rótulo é
   o que diz para onde a aba leva. */
</style>
